import React, { useState } from "react";
import { usePlatformAuth } from "@/context/PlatformAuthContext";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Fingerprint } from "lucide-react";

/** Platform admin's own security page — MFA enrol / verify / disable. */
export default function PlatformSecurity() {
  const { api, admin } = usePlatformAuth();
  const [enrol, setEnrol] = useState(null);
  const [code, setCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [busy, setBusy] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/mfa/setup");
      setEnrol(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "MFA setup failed"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/auth/mfa/verify", { code });
      toast.success("MFA enabled — you'll be asked for a code on next login");
      setEnrol(null); setCode("");
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Disable MFA? Your password will be the only barrier to signing in.")) return;
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode });
      toast.success("MFA disabled");
      setDisableCode("");
      window.location.reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disable failed"); }
    finally { setBusy(false); }
  };

  const mfaOn = !!admin?.mfa_enabled;

  return (
    <div className="p-6 space-y-4 max-w-2xl" data-testid="platform-security">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">My account</div>
        <h1 className="text-2xl font-heading font-semibold inline-flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-slate-400" /> My Security
        </h1>
        <p className="text-xs text-slate-500 mt-1">Two-factor authentication is mandatory before production onboarding.</p>
      </div>

      <section className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3" data-testid="mfa-panel">
        <div className="flex items-center justify-between">
          <div className="text-sm inline-flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-emerald-400" />
            <span>Status: {mfaOn ? <span className="text-emerald-300 font-semibold">Enabled ✓</span> : <span className="text-amber-300 font-semibold">Not enabled</span>}</span>
          </div>
        </div>

        {!mfaOn && !enrol && (
          <button onClick={startSetup} disabled={busy} data-testid="mfa-start-setup"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold">
            <KeyRound className="w-3.5 h-3.5" /> {busy ? "Preparing…" : "Enable MFA"}
          </button>
        )}

        {enrol && (
          <div className="space-y-3" data-testid="mfa-enrol-panel">
            <div className="text-xs text-slate-400">Scan this QR with any TOTP app (Google Authenticator, 1Password, Authy…) or paste the secret manually.</div>
            <img src={enrol.qr_data_url} alt="MFA QR" className="w-44 h-44 bg-white p-2 rounded-md" data-testid="mfa-qr" />
            <div className="text-[11px] font-mono text-slate-300 bg-slate-950 border border-slate-800 rounded-md px-2 py-1 inline-block" data-testid="mfa-secret">
              {enrol.secret}
            </div>
            <div className="flex items-center gap-2">
              <input
                inputMode="numeric" maxLength={8}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="h-9 px-3 rounded-md bg-slate-950 border border-slate-800 focus:border-emerald-500 focus:outline-none text-center text-lg tracking-widest font-mono w-40"
                placeholder="123456"
                data-testid="mfa-verify-code"
              />
              <button onClick={verify} disabled={busy || code.length < 6} data-testid="mfa-verify-submit"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold">
                {busy ? "Verifying…" : "Verify & enable"}
              </button>
            </div>
          </div>
        )}

        {mfaOn && (
          <div className="space-y-2 pt-3 border-t border-slate-800">
            <div className="text-xs text-slate-400">To disable MFA, enter a current code:</div>
            <div className="flex items-center gap-2">
              <input inputMode="numeric" maxLength={8} value={disableCode}
                     onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ""))}
                     className="h-9 px-3 rounded-md bg-slate-950 border border-slate-800 focus:border-rose-500 focus:outline-none text-center text-lg tracking-widest font-mono w-40"
                     placeholder="123456"
                     data-testid="mfa-disable-code" />
              <button onClick={disable} disabled={busy || disableCode.length < 6} data-testid="mfa-disable-submit"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50 text-xs font-semibold">
                Disable MFA
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
